import {useEffect} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {offersReadAloud} from '#/lib/spokenKinds'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  usePlayContentPadding,
  usePlayFooterStyle,
  usePlayPrimaryAction,
  usePlayStackGap,
  usePlayTextStyle,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {FlashBookReadAloud} from '#/components/flashbooks/FlashBookReadAloud'
import {FlashBookRichBlock} from '#/components/flashbooks/FlashBookRichBlock'

export function ContentSlidePlay({
  slide,
  onAnswer,
  primaryColor,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  // The theme's design language, so a rich block is printed the way its theme
  // prints it (the pull quote, the note, the captioned figure, the table) and the
  // read-aloud player wears the theme's own shape.
  const design = useFlashBookPlayDesign()
  // A reading block is the printed page itself, so its heading and its body are
  // set in the book's own type — chapter heading and reading text — rather than
  // in the app's UI font. Empty outside a book.
  const titleText = usePlayTextStyle('title')
  const bodyText = usePlayTextStyle('body')
  const padding = usePlayContentPadding(24)
  // The action strip is page furniture: on ruled paper it is the button alone,
  // printed on the lines, a whole number of rules tall.
  const footerStyle = usePlayFooterStyle()
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // A whole number of the page's rules between the heading and the reading.
  const stackGap = usePlayStackGap(16)

  useEffect(() => {
    // Content slides are not scored; answer is auto-reported after mount
    // so the player knows this slide type doesn't require user interaction
  }, [])

  const handleContinue = () => {
    onAnswer({isCorrect: true, points: 0, maxPoints: 0})
  }

  const kind = slide.contentKind ?? 'text'
  const isRich = kind !== 'text'
  const title = slide.contentTitle ?? ''
  const body = slide.contentText ?? ''
  // Whether the creator gave this block a voice: writing rather than notation,
  // *and* a clip they recorded or uploaded. One rule, asked of the module that
  // owns it (see `#/lib/spokenKinds`), so both studios and both play surfaces
  // answer identically — and a passage nobody narrated has nothing to play.
  const spoken = offersReadAloud(slide)

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={[ss.scrollContent, {padding, gap: stackGap}]}>
        {isRich ? (
          /*
           * A rich reading block is *printed*, not described: the theme draws the
           * pull quote, the note, the captioned figure, the table or the formula
           * in its own treatment. Flattened into a title and a paragraph — which
           * is what this screen did before the capsule could hold one — a table
           * arrived as a sentence and a quotation as ordinary prose.
           */
          <FlashBookRichBlock
            block={slide}
            design={design}
            accent={primaryColor}
          />
        ) : (
          <>
            {!!title && (
              <Text style={[ss.title, titleText, {color: palette.text}]}>
                {title}
              </Text>
            )}

            {!!body && (
              <Text style={[ss.body, bodyText, {color: palette.body}]}>
                {body}
              </Text>
            )}
          </>
        )}

        {/*
         * The reading voice: the clip the creator recorded or uploaded. It is a
         * *transport* — play, a scrub-able measure, a clock — rather than a button
         * that only said "Lecture", because reading aloud is a time-based action
         * and a learner needs to see where they are in it. A block the creator
         * never narrated draws none of this.
         */}
        {spoken && (
          <FlashBookReadAloud
            source={{url: slide.readingAudioUrl!}}
            accent={primaryColor}
            mutedColor={palette.muted}
            design={design}
          />
        )}
      </PlayScroller>

      <View style={[ss.footer, {borderTopColor: palette.border}, footerStyle]}>
        {/* A "Continue" that only scrolls the page it is already on is the kind
            of action the reader learns to distrust, so this one names what it
            actually does. */}
        <TouchableOpacity
          style={[
            ss.continueBtn,
            {backgroundColor: primaryColor},
            action.style,
          ]}
          onPress={handleContinue}
          accessibilityRole="button"
          accessibilityLabel={l`Mark as read`}
          accessibilityHint={undefined}>
          <Text
            style={[
              ss.continueBtnText,
              {color: action.ink(true) ?? palette.onAccent},
              action.labelStyle(true),
            ]}>
            <Trans>Mark as read</Trans>
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const ss = StyleSheet.create({
  root: {
    flex: 1,
  },
  scroll: {flex: 1},
  scrollContent: {
    padding: 24,
    gap: 16,
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    marginTop: 12,
    lineHeight: 34,
  },
  body: {
    fontSize: 16,
    color: PLAY_SURFACE.textFaint,
    lineHeight: 26,
    marginTop: 8,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
  },
  continueBtn: {
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
  },
  continueBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
})
